// React and Router
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

// Firebase
import { auth, logout } from "../../firebase.js";
import { onAuthStateChanged } from "firebase/auth";

// Styles — reuse existing Account styling
import "../../css/Account.css";

/**
 * Standalone /delete-account page.
 *
 * Lives at a clean URL (no Firebase UID in the path) so the link can be
 * shared with the Google Play Store as the data deletion location for the
 * BBTM mobile app. Pulls the UID from the currently signed-in user.
 */
function DeleteAccount() {
    const navigate = useNavigate();

    const [authChecked, setAuthChecked] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [confirmText, setConfirmText] = useState("");
    const [isDeleting, setIsDeleting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user || null);
            setAuthChecked(true);
        });
        return () => unsubscribe();
    }, []);

    const handleDelete = async () => {
        if (!currentUser) {
            navigate("/login");
            return;
        }

        setErrorMessage("");
        setIsDeleting(true);

        try {
            const token = await currentUser.getIdToken();
            const response = await fetch(
                `https://bluebirddocumentationadmin.pythonanywhere.com/users/${currentUser.uid}/delete-account/`,
                {
                    method: "DELETE",
                    headers: {
                        "Content-Type": "application/json",
                        "auth-token": `Bearer ${token}`,
                        "Authorization": `${token}`,
                    },
                }
            );

            if (!response.ok) {
                let detail = "";
                try {
                    const data = await response.json();
                    detail = data?.error || "";
                } catch (e) {}
                throw new Error(detail || `Server returned ${response.status}`);
            }

            // Sign out locally regardless; the Firebase user has been removed server-side.
            try { await logout(); } catch (e) {}

            alert("Your account has been permanently deleted.");
            navigate("/");
        } catch (err) {
            setErrorMessage(err.message || "Failed to delete account. Please try again or contact support.");
        } finally {
            setIsDeleting(false);
        }
    };

    if (!authChecked) {
        return (
            <div className="Account__shell">
                <main className="Account__main">
                    <p>Loading...</p>
                </main>
            </div>
        );
    }

    return (
        <div className="Account__shell">
            <main className="Account__main">
                <button
                    onClick={() => navigate("/")}
                    className="Account__back-link"
                    title="Back to home"
                    style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                        color: "var(--bb-accent)",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 24,
                        fontSize: "0.9rem",
                    }}
                >
                    <i className="fas fa-arrow-left"></i>
                    <span>Back to home</span>
                </button>

                <div className="Account__section-header">
                    <h2 className="Account__section-title">Delete Your Bluebird Account</h2>
                    <p className="Account__section-desc">
                        This is the official account deletion page for the{" "}
                        <strong>Bluebird Tasks</strong> Android app (package{" "}
                        <code>com.bluebird.tasks</code>) and the{" "}
                        <strong>Bluebird Documentation</strong> web app, both published by{" "}
                        <strong>Bluebird Documentation</strong> as part of the{" "}
                        <strong>Bluebird Suite</strong>. A single Bluebird account is shared
                        across all Bluebird Suite applications — deleting it here permanently
                        removes it from every Bluebird Suite product.
                    </p>
                </div>

                <div style={{ marginBottom: 24 }}>
                    <p style={{ marginBottom: 8 }}><strong>What gets deleted:</strong></p>
                    <ul style={{ paddingLeft: 20, lineHeight: 1.7 }}>
                        <li>Your profile, email, and authentication record</li>
                        <li>All personal documents, pages, walkthroughs, and snapshots</li>
                        <li>All notebooks, drawings, saved strokes, and document markups</li>
                        <li>All task boards, columns, tasks, and comments you own</li>
                        <li>Team workspaces you own (and all content within them)</li>
                        <li>Your membership in workspaces and boards owned by others</li>
                        <li>Audio transcriptions, focus sessions, planner entries, and notifications</li>
                        <li>Uploaded attachments and any active subscription</li>
                    </ul>
                    <p style={{ marginTop: 16, fontWeight: 600 }}>
                        This action cannot be undone.
                    </p>
                </div>

                {!currentUser ? (
                    <div className="Account__field-value-display" style={{ flexDirection: "column", alignItems: "flex-start", gap: 12, padding: 20 }}>
                        <p style={{ margin: 0 }}>
                            You need to be signed in to delete your account.
                        </p>
                        <div style={{ display: "flex", gap: 12 }}>
                            <Link to="/login" className="Account__navigation-buttons" style={{ textDecoration: "none" }}>
                                Sign in
                            </Link>
                            <Link to="/" style={{ alignSelf: "center" }}>
                                Back to home
                            </Link>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="Account__field-row">
                            <label className="Account__field-label">Signed in as</label>
                            <div className="Account__field-value-display">
                                {currentUser.email}
                            </div>
                        </div>

                        <div className="Account__field-row">
                            <label className="Account__field-label" htmlFor="deleteConfirmInput">
                                To confirm, type <strong>DELETE</strong> below
                            </label>
                            <input
                                id="deleteConfirmInput"
                                type="text"
                                className="Account__edit-input"
                                value={confirmText}
                                onChange={(e) => setConfirmText(e.target.value)}
                                placeholder="Type DELETE to confirm"
                                disabled={isDeleting}
                                autoComplete="off"
                            />
                        </div>

                        {errorMessage && (
                            <p style={{ color: "#c0392b", marginBottom: 16 }}>{errorMessage}</p>
                        )}

                        <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
                            <button
                                onClick={handleDelete}
                                disabled={isDeleting || confirmText.trim() !== "DELETE"}
                                className="Account__navigation-buttons"
                                style={{
                                    background: "#c0392b",
                                    cursor: (isDeleting || confirmText.trim() !== "DELETE") ? "not-allowed" : "pointer",
                                    opacity: (isDeleting || confirmText.trim() !== "DELETE") ? 0.6 : 1,
                                }}
                            >
                                {isDeleting ? "Deleting..." : "Permanently delete my account"}
                            </button>
                            <button
                                onClick={() => navigate("/")}
                                disabled={isDeleting}
                                className="Account__navigation-buttons"
                            >
                                Cancel and go home
                            </button>
                        </div>
                    </>
                )}
            </main>
        </div>
    );
}

export default DeleteAccount;
